import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { proofExpirySeconds } from "@/lib/payment-proof";

// Shared admin check: relies on user_roles via RLS-scoped client.
// has_role is a SECURITY DEFINER function callable as the user, but to keep
// it simple we just query user_roles for the current user.
async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error || !data) {
    throw new Error("Forbidden: admin access required");
  }
}

export type StudentProgressRow = {
  user_id: string;
  ds160_drafted: boolean;
  mocks_completed: boolean;
  ready_for_interview: boolean;
};

// --- Progress -------------------------------------------------------------

export const updateStudentProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      userId: string;
      ds160_drafted?: boolean;
      mocks_completed?: boolean;
      ready_for_interview?: boolean;
    }) => {
      if (!data.userId || typeof data.userId !== "string") {
        throw new Error("userId required");
      }
      return data;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);

    const update: Record<string, boolean> = {};
    if (typeof data.ds160_drafted === "boolean") update.ds160_drafted = data.ds160_drafted;
    if (typeof data.mocks_completed === "boolean") update.mocks_completed = data.mocks_completed;
    if (typeof data.ready_for_interview === "boolean")
      update.ready_for_interview = data.ready_for_interview;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("student_progress")
      .upsert({ user_id: data.userId, ...update }, { onConflict: "user_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listAllStudentProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("student_progress")
      .select("user_id, ds160_drafted, mocks_completed, ready_for_interview");
    if (error) throw new Error(error.message);
    return { rows: (data ?? []) as StudentProgressRow[] };
  });

// --- Paid students for admin progress view --------------------------------

export type StudentMilestones = {
  ds160_review_done: boolean;
  ds160_review_requested: boolean;
  mocks_done: number;
  mocks_requested: number;
  ready_for_interview: boolean;
  admin_notes: string | null;
  mock_target_override: number | null;
  sevis_paid: boolean;
};

export type PaidStudent = {
  /** payment id - unique per paid record, used as the milestone key */
  payment_id: string;
  user_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  plan_name: string;
  interview_date: string | null;
  created_at: string | null;
  interview_status: "pending" | "approved" | "refused" | "admin_processing";
  milestones: StudentMilestones;
  /** Intake from the student's onboarding form, if provided. */
  intake_term?: string | null;
  /** Payment details (first record) + sum across all merged paid records. */
  amount_paise?: number;
  total_paid_paise?: number;
  payments_count?: number;
  razorpay_order_id?: string | null;
  razorpay_payment_id?: string | null;
  /** Onboarding form details. */
  university?: string | null;
  program?: string | null;
  target_degree?: string | null;
  consulate?: string | null;
  biometrics_city?: string | null;
  biometrics_date?: string | null;
  funding_source?: string | null;
  prior_applicant?: boolean | null;
  prior_refusal_count?: number | null;
  prior_refusal_consulate?: string | null;
};

export const PLAN_MOCK_COUNT: Record<string, number> = {
  "Basic Support": 2,
  "Full Support": 4,
  "Refusal Support": 5,
};

export function effectiveMockTotal(planName: string, override: number | null | undefined): number {
  if (override && override > 0) return override;
  return PLAN_MOCK_COUNT[planName] ?? 2;
}

function emptyMilestones(): StudentMilestones {
  return {
    ds160_review_done: false,
    ds160_review_requested: false,
    mocks_done: 0,
    mocks_requested: 0,
    ready_for_interview: false,
    admin_notes: null,
    mock_target_override: null,
    sevis_paid: false,
  };
}

/**
 * Fast version of listPaidStudents for dashboard summaries: same dedupe and
 * intake-term overlay, but skips milestones and full intake dossiers.
 */
export const listPaidStudentsLite = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [paysRes, ciRes, prRes] = await Promise.all([
      supabaseAdmin
        .from("payments")
        .select("id, user_id, buyer_name, buyer_email, buyer_phone, plan_name, interview_date, created_at, interview_status")
        .eq("status", "paid")
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("client_intakes")
        .select("email, intake_term, submitted_at")
        .not("intake_term", "is", null)
        .order("submitted_at", { ascending: false }),
      supabaseAdmin.from("profiles").select("user_id, intake_term").not("intake_term", "is", null),
    ]);
    if (paysRes.error) throw new Error(paysRes.error.message);

    const termByEmail = new Map<string, string>();
    for (const r of ciRes.data ?? []) {
      const e = (r.email ?? "").toLowerCase();
      if (r.intake_term && !termByEmail.has(e)) termByEmail.set(e, r.intake_term);
    }
    const termByUser = new Map<string, string>();
    for (const r of prRes.data ?? []) if (r.intake_term) termByUser.set(r.user_id, r.intake_term);

    const seen = new Map<string, PaidStudent>();
    for (const p of paysRes.data ?? []) {
      const email = (p.buyer_email ?? "").trim().toLowerCase();
      const phone = (p.buyer_phone ?? "").replace(/\D/g, "");
      const key = (email && `e:${email}`) || (phone && `p:${phone}`) || `pay:${p.id}`;
      const existing = seen.get(key);
      if (existing) {
        if (!existing.user_id && p.user_id) existing.user_id = p.user_id;
        if (!existing.interview_date && p.interview_date) existing.interview_date = p.interview_date;
        if (existing.interview_status === "pending" && p.interview_status && p.interview_status !== "pending")
          existing.interview_status = p.interview_status as PaidStudent["interview_status"];
        continue;
      }
      seen.set(key, {
        payment_id: p.id,
        user_id: p.user_id ?? null,
        name: p.buyer_name ?? "Student",
        email: p.buyer_email,
        phone: p.buyer_phone,
        plan_name: p.plan_name,
        interview_date: p.interview_date ?? null,
        created_at: p.created_at ?? null,
        interview_status: (p.interview_status ?? "pending") as PaidStudent["interview_status"],
        milestones: emptyMilestones(),
      });
    }
    const students = Array.from(seen.values());
    for (const s of students) {
      s.intake_term =
        termByEmail.get((s.email ?? "").toLowerCase()) ?? (s.user_id ? termByUser.get(s.user_id) : undefined) ?? null;
    }
    return { students };
  });

export const listPaidStudents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: pays, error: payErr } = await supabaseAdmin
      .from("payments")
      .select("id, user_id, buyer_name, buyer_email, buyer_phone, plan_name, interview_date, created_at, status, interview_status, amount_paise, razorpay_order_id, razorpay_payment_id")
      .eq("status", "paid")
      .order("created_at", { ascending: false });
    if (payErr) throw new Error(payErr.message);

    // Dedupe by student identity (email > digits-only phone > payment id) — same rule
    // as Monthly Enrollments, so website + manual-import records merge into one student.
    const seen = new Map<string, PaidStudent>();
    for (const p of pays ?? []) {
      const email = (p.buyer_email ?? "").trim().toLowerCase();
      const phone = (p.buyer_phone ?? "").replace(/\D/g, "");
      const key = (email && `e:${email}`) || (phone && `p:${phone}`) || `pay:${p.id}`;
      const existing = seen.get(key);
      if (existing) {
        existing.total_paid_paise = (existing.total_paid_paise ?? 0) + (p.amount_paise ?? 0);
        existing.payments_count = (existing.payments_count ?? 1) + 1;
        // Prefer keeping the linked website account and any known interview info.
        if (!existing.user_id && p.user_id) existing.user_id = p.user_id;
        if (!existing.interview_date && (p as { interview_date?: string | null }).interview_date)
          existing.interview_date = (p as { interview_date?: string | null }).interview_date ?? null;
        if (existing.interview_status === "pending" && p.interview_status && p.interview_status !== "pending")
          existing.interview_status = p.interview_status as PaidStudent["interview_status"];
        continue;
      }
      seen.set(key, {
        payment_id: p.id,
        user_id: p.user_id ?? null,
        name: p.buyer_name ?? "Student",
        email: p.buyer_email,
        phone: p.buyer_phone,
        plan_name: p.plan_name,
        interview_date: (p as { interview_date?: string | null }).interview_date ?? null,
        created_at: (p as { created_at?: string | null }).created_at ?? null,
        interview_status: ((p as { interview_status?: string | null }).interview_status ??
          "pending") as PaidStudent["interview_status"],
        milestones: emptyMilestones(),
        amount_paise: p.amount_paise ?? 0,
        total_paid_paise: p.amount_paise ?? 0,
        payments_count: 1,
        razorpay_order_id: p.razorpay_order_id ?? null,
        razorpay_payment_id: p.razorpay_payment_id ?? null,
      });
    }

    const paymentIds = Array.from(seen.values()).map((s) => s.payment_id);
    if (paymentIds.length > 0) {
      const { data: ms } = await supabaseAdmin
        .from("admin_student_milestones")
        .select(
          "payment_id, ds160_review_done, ds160_review_requested, mocks_done, mocks_requested, ready_for_interview, admin_notes, mock_target_override, sevis_paid",
        )
        .in("payment_id", paymentIds);
      const byId = new Map(ms?.map((r) => [r.payment_id, r]) ?? []);
      for (const s of seen.values()) {
        const row = byId.get(s.payment_id) as
          | {
              ds160_review_done?: boolean;
              ds160_review_requested?: boolean;
              mocks_done?: number;
              mocks_requested?: number;
              ready_for_interview?: boolean;
              admin_notes?: string | null;
              mock_target_override?: number | null;
              sevis_paid?: boolean;
            }
          | undefined;
        if (row) {
          s.milestones = {
            ds160_review_done: !!row.ds160_review_done,
            ds160_review_requested: !!row.ds160_review_requested,
            mocks_done: row.mocks_done ?? 0,
            mocks_requested: row.mocks_requested ?? 0,
            ready_for_interview: !!row.ready_for_interview,
            admin_notes: row.admin_notes ?? null,
            mock_target_override: row.mock_target_override ?? null,
            sevis_paid: !!row.sevis_paid,
          };
        }
      }
    }

    // Overlay onboarding intake term (client_intakes by email, then profiles by user).
    const all = Array.from(seen.values());
    const emails = Array.from(new Set(all.map((s) => (s.email ?? "").trim().toLowerCase()).filter(Boolean)));
    const userIds = Array.from(new Set(all.map((s) => s.user_id).filter(Boolean))) as string[];
    const termByEmail = new Map<string, string>();
    const termByUser = new Map<string, string>();
    type IntakeRow = {
      email: string; intake_term: string | null; university: string | null; program: string | null;
      target_degree: string | null; target_consulate: string | null; visa_city: string | null;
      biometrics_city: string | null; biometrics_date: string | null; funding_source: string | null;
      prior_applicant: boolean | null; prior_refusal_count: number | null; prior_refusal_consulate: string | null;
    };
    const intakeByEmail = new Map<string, IntakeRow>();
    if (emails.length) {
      for (let i = 0; i < emails.length; i += 300) {
        const { data: ci } = await supabaseAdmin
          .from("client_intakes")
          .select("email, intake_term, university, program, target_degree, target_consulate, visa_city, biometrics_city, biometrics_date, funding_source, prior_applicant, prior_refusal_count, prior_refusal_consulate, submitted_at")
          .in("email", emails.slice(i, i + 300))
          .order("submitted_at", { ascending: false });
        for (const r of (ci ?? []) as IntakeRow[]) {
          const e = (r.email ?? "").toLowerCase();
          if (r.intake_term && !termByEmail.has(e)) termByEmail.set(e, r.intake_term);
          if (!intakeByEmail.has(e)) intakeByEmail.set(e, r);
        }
      }
    }
    if (userIds.length) {
      const { data: pr } = await supabaseAdmin
        .from("profiles")
        .select("user_id, intake_term")
        .in("user_id", userIds)
        .not("intake_term", "is", null);
      for (const r of pr ?? []) if (r.intake_term) termByUser.set(r.user_id, r.intake_term);
    }
    for (const s of all) {
      s.intake_term =
        termByEmail.get((s.email ?? "").toLowerCase()) ?? (s.user_id ? termByUser.get(s.user_id) : undefined) ?? null;
      const ir = intakeByEmail.get((s.email ?? "").toLowerCase());
      if (ir) {
        s.university = ir.university;
        s.program = ir.program;
        s.target_degree = ir.target_degree;
        s.consulate = ir.target_consulate ?? ir.visa_city;
        s.biometrics_city = ir.biometrics_city;
        s.biometrics_date = ir.biometrics_date;
        s.funding_source = ir.funding_source;
        s.prior_applicant = ir.prior_applicant;
        s.prior_refusal_count = ir.prior_refusal_count;
        s.prior_refusal_consulate = ir.prior_refusal_consulate;
      }
    }

    return { students: all };
  });

export const updateStudentMilestone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      paymentId: string;
      ds160_review_done?: boolean;
      ds160_review_requested?: boolean;
      mocks_done?: number;
      mocks_requested?: number;
      ready_for_interview?: boolean;
      admin_notes?: string | null;
      mock_target_override?: number | null;
      sevis_paid?: boolean;
    }) => {
      if (!data?.paymentId) throw new Error("paymentId required");
      return data;
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const update: Record<string, boolean | number | string | null> = {};
    if (typeof data.ds160_review_done === "boolean")
      update.ds160_review_done = data.ds160_review_done;
    if (typeof data.ds160_review_requested === "boolean")
      update.ds160_review_requested = data.ds160_review_requested;
    if (typeof data.mocks_done === "number") update.mocks_done = data.mocks_done;
    if (typeof data.mocks_requested === "number") update.mocks_requested = data.mocks_requested;
    if (typeof data.ready_for_interview === "boolean")
      update.ready_for_interview = data.ready_for_interview;
    if (data.admin_notes !== undefined) update.admin_notes = data.admin_notes;
    if (data.mock_target_override !== undefined)
      update.mock_target_override = data.mock_target_override;
    if (typeof data.sevis_paid === "boolean") update.sevis_paid = data.sevis_paid;
    const { error } = await supabaseAdmin
      .from("admin_student_milestones")
      .upsert({ payment_id: data.paymentId, ...update }, { onConflict: "payment_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });


// --- Student-facing: read own milestones & flag mock/DS requests ---------

export type MyMilestonesResult = {
  found: boolean;
  payment_id: string | null;
  plan_name: string | null;
  milestones: StudentMilestones;
};

export const getMyMilestones = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyMilestonesResult> => {
    const { supabase, userId } = context;
    const { data: pay } = await supabase
      .from("payments")
      .select("id, plan_name")
      .eq("user_id", userId)
      .eq("status", "paid")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!pay) {
      return { found: false, payment_id: null, plan_name: null, milestones: emptyMilestones() };
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("admin_student_milestones")
      .select(
        "ds160_review_done, ds160_review_requested, mocks_done, mocks_requested, ready_for_interview",
      )
      .eq("payment_id", pay.id)
      .maybeSingle();
    const r = row as
      | {
          ds160_review_done?: boolean;
          ds160_review_requested?: boolean;
          mocks_done?: number;
          mocks_requested?: number;
          ready_for_interview?: boolean;
        }
      | null;
    return {
      found: true,
      payment_id: pay.id,
      plan_name: pay.plan_name,
      milestones: {
        ds160_review_done: !!r?.ds160_review_done,
        ds160_review_requested: !!r?.ds160_review_requested,
        mocks_done: r?.mocks_done ?? 0,
        mocks_requested: r?.mocks_requested ?? 0,
        ready_for_interview: !!r?.ready_for_interview,
        admin_notes: null,
        mock_target_override: null,
        sevis_paid: false,
      },

    };
  });

export const requestMyMilestone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { kind: "ds160" | "mock"; mockNumber?: number }) => {
    if (data?.kind !== "ds160" && data?.kind !== "mock") throw new Error("Invalid kind");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: pay } = await supabase
      .from("payments")
      .select("id")
      .eq("user_id", userId)
      .eq("status", "paid")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!pay) throw new Error("No paid plan found");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("admin_student_milestones")
      .select("mocks_requested, ds160_review_requested")
      .eq("payment_id", pay.id)
      .maybeSingle();
    const cur = (row ?? {}) as { mocks_requested?: number; ds160_review_requested?: boolean };

    const update: Record<string, boolean | number> = {};
    if (data.kind === "ds160") {
      update.ds160_review_requested = true;
    } else {
      const target = Math.max(
        cur.mocks_requested ?? 0,
        data.mockNumber ?? (cur.mocks_requested ?? 0) + 1,
      );
      update.mocks_requested = target;
    }
    const { error } = await supabaseAdmin
      .from("admin_student_milestones")
      .upsert({ payment_id: pay.id, ...update }, { onConflict: "payment_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });


// --- Incomplete checkouts -------------------------------------------------

export type IncompleteUser = {
  id: string;
  email: string | null;
  name: string | null;
  created_at: string;
  phone: string | null;
  last_plan: string | null;
};

export const listIncompleteCheckouts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Get all user_ids that have a paid payment
    const { data: paid, error: paidErr } = await supabaseAdmin
      .from("payments")
      .select("user_id")
      .eq("status", "paid");
    if (paidErr) throw new Error(paidErr.message);
    const paidIds = new Set<string>();
    for (const r of paid ?? []) if (r.user_id) paidIds.add(r.user_id);

    // Phone from profiles; last attempted plan + phone from unpaid payment attempts.
    const [{ data: profs }, { data: attempts }] = await Promise.all([
      supabaseAdmin.from("profiles").select("user_id, phone"),
      supabaseAdmin
        .from("payments")
        .select("user_id, plan_name, buyer_phone, created_at")
        .neq("status", "paid")
        .not("user_id", "is", null)
        .order("created_at", { ascending: false }),
    ]);
    const phoneByUser = new Map<string, string>();
    for (const p of profs ?? []) if (p.user_id && p.phone) phoneByUser.set(p.user_id, p.phone);
    const attemptByUser = new Map<string, { plan: string; phone: string | null }>();
    for (const a of attempts ?? []) {
      if (!a.user_id || attemptByUser.has(a.user_id)) continue;
      attemptByUser.set(a.user_id, {
        plan: a.plan_name,
        phone: a.buyer_phone && a.buyer_phone !== "not-provided" ? a.buyer_phone : null,
      });
    }

    // List auth users (paginate up to ~5000)
    const users: IncompleteUser[] = [];
    let page = 1;
    while (page < 50) {
      const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 200 });
      if (error) throw new Error(error.message);
      const batch = data?.users ?? [];
      for (const u of batch) {
        if (paidIds.has(u.id)) continue;
        const meta = (u.user_metadata ?? {}) as Record<string, unknown>;
        const attempt = attemptByUser.get(u.id);
        users.push({
          id: u.id,
          email: u.email ?? null,
          name:
            (meta.full_name as string) ||
            (meta.name as string) ||
            (u.email?.split("@")[0] ?? null),
          created_at: u.created_at,
          phone: phoneByUser.get(u.id) ?? attempt?.phone ?? null,
          last_plan: attempt?.plan ?? null,
        });
      }
      if (batch.length < 200) break;
      page += 1;
    }

    users.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return { users };
  });

// --- Inbox / messaging ----------------------------------------------------

export type InboxThread = {
  user_id: string;
  name: string;
  email: string | null;
  last_message: string;
  last_message_at: string;
  last_from_admin: boolean;
  last_student_message_at: string | null;
  unread_count: number;
  last_read_at: string | null;
  phone: string | null;
  plan: string | null;
  is_paid: boolean;
};

export const listInboxThreads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: msgs, error } = await supabaseAdmin
      .from("student_messages")
      .select("user_id, content, created_at, is_from_admin")
      .order("created_at", { ascending: false })
      .limit(2000);
    if (error) throw new Error(error.message);

    type Mut = InboxThread & { _studentTimes: string[] };
    const byUser = new Map<string, Mut>();
    let latestStudentAt: string | null = null;
    for (const m of msgs ?? []) {
      if (!byUser.has(m.user_id)) {
        byUser.set(m.user_id, {
          user_id: m.user_id,
          name: "Student",
          email: null,
          last_message: m.content,
          last_message_at: m.created_at,
          last_from_admin: m.is_from_admin,
          last_student_message_at: null,
          unread_count: 0,
          last_read_at: null,
          phone: null,
          plan: null,
          is_paid: false,
          _studentTimes: [],
        });
      }
      const t = byUser.get(m.user_id)!;
      if (!m.is_from_admin) {
        if (!t.last_student_message_at) t.last_student_message_at = m.created_at;
        t._studentTimes.push(m.created_at);
        if (!latestStudentAt || m.created_at > latestStudentAt) latestStudentAt = m.created_at;
      }
    }

    const ids = Array.from(byUser.keys());
    if (ids.length > 0) {
      const [{ data: profs }, { data: pays }, { data: reads }] = await Promise.all([
        supabaseAdmin.from("profiles").select("user_id, name, phone").in("user_id", ids),
        supabaseAdmin
          .from("payments")
          .select("user_id, buyer_name, buyer_email, buyer_phone, plan_name, status, created_at")
          .in("user_id", ids)
          .order("created_at", { ascending: false }),
        supabaseAdmin
          .from("admin_thread_reads")
          .select("student_user_id, last_read_at")
          .eq("admin_user_id", userId)
          .in("student_user_id", ids),
      ]);
      for (const p of profs ?? []) {
        if (!p.user_id) continue;
        const t = byUser.get(p.user_id);
        if (t && p.name) t.name = p.name;
        if (t && p.phone && !t.phone) t.phone = p.phone;
      }
      for (const p of pays ?? []) {
        if (!p.user_id) continue;
        const t = byUser.get(p.user_id);
        if (!t) continue;
        if (!t.email && p.buyer_email) t.email = p.buyer_email;
        if (!t.phone && p.buyer_phone) t.phone = p.buyer_phone;
        if (t.name === "Student" && p.buyer_name) t.name = p.buyer_name;
        if (p.status === "paid" && !t.is_paid) {
          t.is_paid = true;
          t.plan = p.plan_name;
        }
      }
      const readMap = new Map<string, string>();
      for (const r of reads ?? []) readMap.set(r.student_user_id, r.last_read_at);
      for (const t of byUser.values()) {
        const lr = readMap.get(t.user_id) ?? null;
        t.last_read_at = lr;
        const lrMs = lr ? new Date(lr).getTime() : 0;
        t.unread_count = t._studentTimes.filter((ts) => new Date(ts).getTime() > lrMs).length;
      }
    }

    const threads: InboxThread[] = Array.from(byUser.values())
      .map(({ _studentTimes, ...rest }) => rest)
      .sort((a, b) => b.last_message_at.localeCompare(a.last_message_at));
    const totalUnread = threads.reduce((s, t) => s + t.unread_count, 0);
    return { threads, latestStudentMessageAt: latestStudentAt, totalUnread };
  });

export const getStudentThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { userId: string }) => {
    if (!data?.userId) throw new Error("userId required");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: msgs, error } = await supabaseAdmin
      .from("student_messages")
      .select("id, content, is_from_admin, created_at")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: true })
      .limit(500);
    if (error) throw new Error(error.message);

    // Payment verification context so admins never have to cross-check manually.
    const [{ data: intake }, { data: pays }, { data: prof }] = await Promise.all([
      supabaseAdmin
        .from("client_intakes")
        .select(
          "full_name, email, whatsapp, university, program, plan, payment_proof_url, payment_proof_path, biometrics_city, biometrics_date, visa_city, visa_date, has_booked_interview, admin_status, submitted_at, target_degree, intake_term, target_consulate, funding_source, prior_refusal_count, raw_data",
        )
        .eq("user_id", data.userId)
        .order("submitted_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabaseAdmin
        .from("payments")
        .select(
          "id, plan_name, amount_paise, status, razorpay_payment_id, razorpay_order_id, buyer_name, buyer_email, buyer_phone, created_at, receipt_url, interview_date",
        )
        .eq("user_id", data.userId)
        .order("created_at", { ascending: false })
        .limit(10),
      supabaseAdmin
        .from("profiles")
        .select("name, phone, university, target_degree, target_consulate, intake_term, consulate")
        .eq("user_id", data.userId)
        .maybeSingle(),
    ]);

    // Always hand the admin a live verification URL: re-sign the private
    // receipt object (expiry = time until the interview date, else 7 days).
    let paymentProofUrl: string | null = (intake as { payment_proof_url?: string | null } | null)?.payment_proof_url ?? null;
    const proofPath = (intake as { payment_proof_path?: string | null } | null)?.payment_proof_path ?? null;
    if (proofPath) {
      const { data: signed } = await supabaseAdmin.storage
        .from("payment-proofs")
        .createSignedUrl(proofPath, proofExpirySeconds(intake?.visa_date ?? null));
      if (signed?.signedUrl) paymentProofUrl = signed.signedUrl;
    }

    // Mark the thread as read for this admin.
    await supabaseAdmin.from("admin_thread_reads").upsert(
      {
        admin_user_id: userId,
        student_user_id: data.userId,
        last_read_at: new Date().toISOString(),
      } as never,
      { onConflict: "admin_user_id,student_user_id" as never },
    );

    return {
      messages: msgs ?? [],
      student: {
        name: intake?.full_name ?? prof?.name ?? pays?.[0]?.buyer_name ?? "Student",
        email: intake?.email ?? pays?.[0]?.buyer_email ?? null,
        phone: intake?.whatsapp ?? prof?.phone ?? pays?.[0]?.buyer_phone ?? null,
        university: intake?.university ?? prof?.university ?? null,
        degree: intake?.target_degree ?? intake?.program ?? prof?.target_degree ?? null,
        consulate: intake?.target_consulate ?? intake?.visa_city ?? prof?.target_consulate ?? prof?.consulate ?? null,
        biometricsCity: intake?.biometrics_city ?? null,
        intakeTerm: intake?.intake_term ?? prof?.intake_term ?? null,
        fundingSource: intake?.funding_source ?? null,
        priorRefusals: intake?.prior_refusal_count ?? null,
        intakeAnswers: (() => {
          const raw = (intake?.raw_data ?? null) as Record<string, unknown> | null;
          if (!raw || typeof raw !== "object") return [] as { q: string; a: string }[];
          return Object.entries(raw)
            .filter(([, v]) => v !== null && v !== "" && typeof v !== "object")
            .slice(0, 40)
            .map(([q, v]) => ({ q, a: String(v) }));
        })(),
        plan: intake?.plan ?? pays?.[0]?.plan_name ?? null,
        paymentProofUrl,
        biometricsDate: intake?.biometrics_date ?? null,
        visaDate: intake?.visa_date ?? pays?.find((p: any) => p.interview_date)?.interview_date ?? null,
        hasBookedInterview: intake?.has_booked_interview ?? null,
        intakeStatus: intake?.admin_status ?? null,
      },
      payments: (pays ?? []).map((p: any) => ({
        id: p.id,
        planName: p.plan_name,
        amountInr: Math.round((p.amount_paise ?? 0) / 100),
        status: p.status,
        razorpayPaymentId: p.razorpay_payment_id,
        razorpayOrderId: p.razorpay_order_id,
        receiptUrl: (p.receipt_url as string | null) ?? null,
        createdAt: p.created_at,
      })),
    };
  });

export const sendAdminMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { userId: string; content: string }) => {
    if (!data?.userId) throw new Error("userId required");
    const content = (data.content ?? "").trim();
    if (!content) throw new Error("Message required");
    if (content.length > 4000) throw new Error("Message too long");
    return { userId: data.userId, content };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("student_messages").insert({
      user_id: data.userId,
      content: data.content,
      is_from_admin: true,
    });
    if (error) throw new Error(error.message);
    // Auto-mark this thread as read for the admin when they reply
    await supabaseAdmin
      .from("admin_thread_reads")
      .upsert(
        { admin_user_id: userId, student_user_id: data.userId, last_read_at: new Date().toISOString() },
        { onConflict: "admin_user_id,student_user_id" },
      );
    return { ok: true };
  });

export const markThreadRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { userId: string }) => {
    if (!data?.userId) throw new Error("userId required");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("admin_thread_reads")
      .upsert(
        { admin_user_id: userId, student_user_id: data.userId, last_read_at: new Date().toISOString() },
        { onConflict: "admin_user_id,student_user_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const markAllThreadsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await assertAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: distinct, error: distErr } = await supabaseAdmin
      .from("student_messages")
      .select("user_id")
      .eq("is_from_admin", false)
      .limit(5000);
    if (distErr) throw new Error(distErr.message);
    const ids = Array.from(new Set((distinct ?? []).map((r) => r.user_id).filter(Boolean)));
    if (ids.length === 0) return { ok: true, count: 0 };
    const now = new Date().toISOString();
    const rows = ids.map((sid) => ({
      admin_user_id: userId,
      student_user_id: sid,
      last_read_at: now,
    }));
    const { error } = await supabaseAdmin
      .from("admin_thread_reads")
      .upsert(rows, { onConflict: "admin_user_id,student_user_id" });
    if (error) throw new Error(error.message);
    return { ok: true, count: ids.length };
  });


// -------- Admin: full onboarding profile + payment receipt ----------------

export type StudentOnboardingDetails = {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  university: string | null;
  target_degree: string | null;
  intake_term: string | null;
  target_consulate: string | null;
  booked_visa_slots: string | null;
  visa_history: string | null;
  biometrics_date: string | null;
  visa_date: string | null;
  payment_proof_url: string | null;
};

/**
 * Everything an admin needs for the student drawer: onboarding answers plus a
 * freshly signed receipt URL (falls back to the stored public URL).
 */
export const getStudentOnboardingDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { email?: string | null; userId?: string | null }) => ({
    email: (i?.email ?? "").trim().toLowerCase() || null,
    userId: i?.userId ?? null,
  }))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let intake: Record<string, unknown> | null = null;
    if (data.email) {
      const { data: byEmail } = await supabaseAdmin
        .from("client_intakes")
        .select("*")
        .eq("email", data.email)
        .order("submitted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      intake = (byEmail as Record<string, unknown> | null) ?? null;
    }
    if (!intake && data.userId) {
      const { data: byUser } = await supabaseAdmin
        .from("client_intakes")
        .select("*")
        .eq("user_id", data.userId)
        .order("submitted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      intake = (byUser as Record<string, unknown> | null) ?? null;
    }

    let profile: Record<string, unknown> | null = null;
    if (data.userId) {
      const { data: p } = await supabaseAdmin
        .from("profiles")
        .select("*")
        .eq("user_id", data.userId)
        .maybeSingle();
      profile = (p as Record<string, unknown> | null) ?? null;
    }

    const pick = (key: string): string | null => {
      const a = intake?.[key];
      if (typeof a === "string" && a.trim()) return a;
      const b = profile?.[key];
      if (typeof b === "string" && b.trim()) return b;
      return null;
    };

    let proofUrl = pick("payment_proof_url");
    const proofPath = typeof intake?.["payment_proof_path"] === "string" ? (intake["payment_proof_path"] as string) : null;
    if (proofPath) {
      const { data: signed } = await supabaseAdmin.storage
        .from("payment-proofs")
        .createSignedUrl(proofPath, 60 * 60 * 24 * 7);
      if (signed?.signedUrl) proofUrl = signed.signedUrl;
    }

    const details: StudentOnboardingDetails = {
      full_name: pick("full_name") ?? (typeof profile?.["name"] === "string" ? (profile["name"] as string) : null),
      email: pick("email") ?? data.email,
      phone: pick("whatsapp") ?? (typeof profile?.["phone"] === "string" ? (profile["phone"] as string) : null),
      university: pick("university"),
      target_degree: pick("target_degree"),
      intake_term: pick("intake_term"),
      target_consulate: pick("target_consulate") ?? (typeof profile?.["consulate"] === "string" ? (profile["consulate"] as string) : null),
      booked_visa_slots: pick("has_booked_interview") ?? pick("booked_visa_slots"),
      visa_history: pick("visa_history"),
      biometrics_date: pick("biometrics_date"),
      visa_date: pick("visa_date"),
      payment_proof_url: proofUrl,
    };

    return { details };
  });

// --- Enrollment date edit -------------------------------------------------

export const updatePaymentEnrollmentDate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { paymentId: string; date: string }) => {
    if (!/^[0-9a-f-]{36}$/i.test(data?.paymentId ?? "")) throw new Error("Invalid payment id");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data?.date ?? "")) throw new Error("Invalid date");
    const y = Number(data.date.slice(0, 4));
    if (y < 2015 || y > 2035) throw new Error("Date out of range");
    return data;
  })
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("payments")
      .update({ created_at: `${data.date}T12:00:00Z` })
      .eq("id", data.paymentId)
      .eq("status", "paid");
    if (error) throw new Error(error.message);
    return { ok: true };
  });
